import React from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { usePricingCampaignQuery } from '@/entities/pricing-campaign'

import { ActivityView } from '@/widgets/pricing-campaign/activity'
import { AnchoringRulesView } from '@/widgets/pricing-campaign/anchoring'
import { AssignmentView } from '@/widgets/pricing-campaign/assignment'
import { MinimalPriceStepView } from '@/widgets/pricing-campaign/minimal-steps'
import { PriceLimitsView } from '@/widgets/pricing-campaign/price-limits'
import { PricingTacticsView } from '@/widgets/pricing-campaign/pricing-tactics'
import { RoundingRulesView } from '@/widgets/pricing-campaign/rounding-rules'

import {
	Alert,
	AlertDescription,
	AlertTitle,
} from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'

export const ScenarioPricingCampaignTab = ({
	pricingCampaignId,
}: {
	pricingCampaignId: string
}) => {
	const { t } = useTranslation()

	const pricingCampaignsQuery = usePricingCampaignQuery(pricingCampaignId, {
		enabled: !!pricingCampaignId,
	})
	if (pricingCampaignsQuery.isLoading) {
		return (
			<div className='flex w-full flex-col gap-2 p-4'>
				<Skeleton className='h-32 w-full' />
				<Skeleton className='h-48 w-full opacity-30' />
				<Skeleton className='h-52 w-full opacity-10' />
			</div>
		)
	}
	if (pricingCampaignsQuery.isError) {
		return (
			<div className='w-full p-4'>
				<Alert variant='destructive'>
					<AlertTriangleIcon />
					<AlertTitle>
						{t('app.unexpected_error', { defaultValue: 'Unexpected error' })}
					</AlertTitle>
					<AlertDescription>
						{t('app.pricing campaign_fetch_error', {
							defaultValue:
								'Something went wrong while fetching the pricing campaign, please try again later or contact support.',
						})}
					</AlertDescription>
				</Alert>
			</div>
		)
	}
	if (!pricingCampaignsQuery.data) {
		return null
	}

	return (
		<div className='flex flex-col gap-4 overflow-y-auto px-4 pt-2 pb-4'>
			<ActivityView settings={pricingCampaignsQuery.data.settings.activity} />
			<AssignmentView
				assignments={pricingCampaignsQuery.data.settings.product_assignments}
			/>
			<PricingTacticsView
				settings={pricingCampaignsQuery.data.settings.pricing_tactics}
			/>
			<PriceLimitsView
				settings={pricingCampaignsQuery.data.settings.price_limits}
			/>
			<AnchoringRulesView
				settings={pricingCampaignsQuery.data.settings.anchoring}
			/>
			<RoundingRulesView
				settings={pricingCampaignsQuery.data.settings.rounding}
			/>
			<MinimalPriceStepView
				settings={pricingCampaignsQuery.data.settings.minimal_steps}
			/>
		</div>
	)
}
